<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Approvals</h5>
        <p class="page-sub">Visits awaiting your approval — scheduled visitors whose type requires sign-off before entry</p>
      </div>
      <button class="btn-ghost" @click="load"><font-awesome-icon icon="sync-alt" /> Refresh</button>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <div v-else-if="!approvals.data?.length && !approvals.length" class="empty-state">
      <font-awesome-icon icon="check-double" class="empty-icon empty-icon--green" />
      <p class="empty-title">No pending approvals</p>
      <p class="empty-sub">All visitor requests have been processed. New requests requiring your approval will appear here.</p>
    </div>

    <div v-else class="approvals-list">
      <div v-for="a in (approvals.data ?? approvals)" :key="a.id" class="approval-card">
        <div class="approval-header">
          <div class="approval-visitor-info">
            <div class="visitor-avatar">{{ initials(a.visit?.visitor) }}</div>
            <div>
              <div class="visitor-name">{{ fullName(a.visit?.visitor) }}</div>
              <div class="visitor-meta">
                <span v-if="a.visit?.visitor?.company">{{ a.visit.visitor.company }} · </span>
                <span>{{ a.visit?.purpose }}</span>
              </div>
            </div>
          </div>
          <div class="approval-due" :class="isOverdue(a) ? 'due--overdue' : 'due--normal'">
            <font-awesome-icon :icon="isOverdue(a) ? 'exclamation-circle' : 'clock'" />
            <span>{{ isOverdue(a) ? 'Overdue' : `Due ${formatRelative(a.due_at)}` }}</span>
          </div>
        </div>
        <div class="approval-body">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Host</span>
              <span class="info-val">{{ fullName(a.visit?.host) ?? a.visit?.host_name ?? '—' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Scheduled</span>
              <span class="info-val">{{ formatDt(a.visit?.scheduled_at) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Location</span>
              <span class="info-val">{{ a.visit?.location ?? '—' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Step</span>
              <span class="info-val">Step {{ a.step }}</span>
            </div>
          </div>
          <div v-if="a.visit?.note" class="approval-note">
            <font-awesome-icon icon="sticky-note" /> {{ a.visit.note }}
          </div>
        </div>
        <div class="approval-footer">
          <div style="flex:1;">
            <textarea v-model="comments[a.id]" class="comment-input" rows="2" placeholder="Add a comment (optional)…"></textarea>
          </div>
          <div class="approval-btns">
            <button class="btn-reject" @click="reject(a)" :disabled="actionId === a.id">
              <font-awesome-icon v-if="actionId === a.id && actionType === 'reject'" icon="spinner" spin />
              <font-awesome-icon v-else icon="times-circle" />
              Reject
            </button>
            <button class="btn-approve" @click="approve(a)" :disabled="actionId === a.id">
              <font-awesome-icon v-if="actionId === a.id && actionType === 'approve'" icon="spinner" spin />
              <font-awesome-icon v-else icon="check-circle" />
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="approvals.last_page > 1" class="pagination-bar">
      <button :disabled="page === 1" class="pg-btn" @click="load(page - 1)">‹ Prev</button>
      <span class="pg-info">Page {{ approvals.current_page }} of {{ approvals.last_page }}</span>
      <button :disabled="page === approvals.last_page" class="pg-btn" @click="load(page + 1)">Next ›</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorApprovals',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      approvals: [],
      page: 1,
      comments: {},
      actionId: null,
      actionType: null,
    }
  },
  created() { this.load() },
  methods: {
    async load(p = 1) {
      this.loading = true; this.page = p
      try {
        const r = await axios.get('visitors/approvals/pending', { params: { page: p } })
        this.approvals = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load approvals.') }
      finally { this.loading = false }
    },
    async approve(a) {
      this.actionId = a.id; this.actionType = 'approve'
      try {
        await axios.post(`visitors/approvals/visit/${a.visit_id}/approve`, { comment: this.comments[a.id] ?? null })
        this.toast.success('Visit approved.')
        delete this.comments[a.id]
        await this.load(this.page)
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to approve.') }
      finally { this.actionId = null; this.actionType = null }
    },
    async reject(a) {
      this.actionId = a.id; this.actionType = 'reject'
      try {
        await axios.post(`visitors/approvals/visit/${a.visit_id}/reject`, { comment: this.comments[a.id] ?? null })
        this.toast.success('Visit rejected.')
        delete this.comments[a.id]
        await this.load(this.page)
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to reject.') }
      finally { this.actionId = null; this.actionType = null }
    },
    fullName(p) { if (!p) return null; return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || null },
    initials(p) { if (!p) return '?'; return ((p.first_name?.[0] ?? '') + (p.last_name?.[0] ?? '')).toUpperCase() || '?' },
    isOverdue(a) { return a.due_at && new Date(a.due_at) < new Date() },
    formatRelative(d) {
      if (!d) return '—'
      const diff = new Date(d) - new Date()
      const h = Math.round(diff / 3600000)
      if (h < 0) return 'overdue'
      if (h < 1) return 'in < 1 hour'
      if (h < 24) return `in ${h}h`
      return `in ${Math.round(h / 24)}d`
    },
    formatDt(d) { return d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—' },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }

.approvals-list { display:flex; flex-direction:column; gap:14px; }
.approval-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.approval-header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 18px; border-bottom:1px solid #f0f4f8; flex-wrap:wrap; }
.approval-visitor-info { display:flex; align-items:center; gap:12px; }
.visitor-avatar { width:38px; height:38px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.visitor-name { font-size:14px; font-weight:700; color:#1a2b45; }
.visitor-meta { font-size:12px; color:#7a90b0; margin-top:2px; }

.approval-due { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; padding:5px 10px; border-radius:20px; }
.due--normal  { background:#f0f9ff; color:#0369a1; }
.due--overdue { background:#fff5f5; color:#dc2626; }

.approval-body { padding:14px 18px; }
.info-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; margin-bottom:10px; }
.info-item { display:flex; flex-direction:column; gap:2px; }
.info-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#aab8cc; }
.info-val   { font-size:13px; color:#1a2b45; font-weight:500; }
.approval-note { background:#fafbfd; border:1px solid #e4eaf2; border-radius:8px; padding:8px 12px; font-size:12px; color:#4a5e78; }

.approval-footer { display:flex; gap:12px; align-items:flex-end; padding:12px 18px; border-top:1px solid #f0f4f8; background:#fafbfd; flex-wrap:wrap; }
.comment-input { width:100%; padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:12px; color:#1a2b45; outline:none; resize:none; box-sizing:border-box; font-family:inherit; }
.comment-input:focus { border-color:#004AAD; }
.approval-btns { display:flex; gap:8px; flex-shrink:0; }

.btn-approve { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-approve:hover:not(:disabled) { background:#15803d; }
.btn-approve:disabled { opacity:.6; cursor:not-allowed; }
.btn-reject  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#dc2626; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:1px solid #fca5a5; cursor:pointer; font-family:inherit; }
.btn-reject:hover:not(:disabled) { background:#fff5f5; }
.btn-reject:disabled { opacity:.6; cursor:not-allowed; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-icon--green { color:#86efac; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; max-width:440px; margin-left:auto; margin-right:auto; }

.pagination-bar { display:flex; align-items:center; gap:10px; padding:14px 0; }
.pg-btn  { padding:6px 14px; border:1px solid #e4eaf2; border-radius:8px; background:#fff; font-size:13px; cursor:pointer; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-info { font-size:13px; color:#7a90b0; flex:1; text-align:center; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
